import * as React from 'react';

import { useFreeTrialCTACopyExperimentData } from 'bundles/coursera-plus-common/components/elevateLTV/startFreeTrial/useGenerateStartFreeTrialTestEpicImpression';
import ArticleRichEntityCard from 'bundles/unified-seo-content-common/components/entity-cards/ArticleRichEntityCard';
import EntityCardPlaceholder from 'bundles/unified-seo-content-common/components/entity-cards/EntityCardPlaceholder';
import type { ENTITY_TYPES } from 'bundles/unified-seo-content-common/constants';
import useEntityCardData from 'bundles/unified-seo-content-common/hooks/useEntityCardData';
import useEntityCardLink from 'bundles/unified-seo-content-common/hooks/useEntityCardLink';
import type { EntityCardInputProps, EntityType } from 'bundles/unified-seo-content-common/types';
import { getEntityTypeLabel } from 'bundles/unified-seo-content-common/utils';
import { getSpecializationQueryConfig } from 'bundles/unified-seo-content-common/utils/entityCardDataQueryUtils';

import _t from 'i18n!nls/articles';

type SpecializationCardEntityType = Extract<EntityType, typeof ENTITY_TYPES.s12n | typeof ENTITY_TYPES.profS12n>;

type Props = EntityCardInputProps & {
  entityType: SpecializationCardEntityType;
};

const SpecializationRichEntityCard = ({ entityType, slug, customDescription }: Props) => {
  const { data, loading } = useEntityCardData(getSpecializationQueryConfig(slug));
  const cardLink = useEntityCardLink({ entityType, slug });
  const { isSessionBasedFreeTrialCopyEnabled, isUserBasedFreeTrialCopyEnabled } = useFreeTrialCTACopyExperimentData({
    s12nId: data?.id,
  });
  const shouldShowStartFreeTrialLabel = isSessionBasedFreeTrialCopyEnabled || isUserBasedFreeTrialCopyEnabled;

  if (loading) {
    return <EntityCardPlaceholder />;
  }

  if (!data || !data.name || !data.partners?.length) {
    return null;
  }

  let subtitle = '';
  if (data.headline) {
    subtitle += data.headline;
  }

  if (data?.subheader) {
    subtitle = subtitle.trim().length > 0 ? subtitle + '. ' + data.subheader : data.subheader;
  }

  const articleRichEntityCardProps = {
    ctaHref: cardLink,
    ctaLabel: shouldShowStartFreeTrialLabel ? _t('Start free trial') : undefined,
    ctaAriaLabel: _t('Learn more about #{name}', { name: data?.name }),
    imageSrc: data.partners[0].squareLogo || '',
    imageAlt: data.partners[0].name,
    title: data.name,
    subtitle: customDescription || subtitle,
    cardMetaData: {
      entityType: getEntityTypeLabel(entityType).toLowerCase(),
      level: data.difficultyLevel || null,
      durationInMonths: data.totalLearningMonths ?? undefined,
      skills: data.specializationAndCourseSkills?.join(', '),
      averageFiveStarRating: data.ratings?.averageFiveStarRating?.toFixed(1),
      ratingCount: data.ratings?.ratingCount,
      totalEnrollmentCount: Number(data.totalEnrollmentCount).toLocaleString(),
    },
  };

  return <ArticleRichEntityCard {...articleRichEntityCardProps} />;
};

export default SpecializationRichEntityCard;
